import { dec, type JobToDateResponse } from '@siteday/shared';
import { useMemo, useState } from 'react';

import { BandPill } from '@/components/BandPill';
import { EmptyState, ErrorState, LoadingState } from '@/components/PageHeader';
import { SummaryTile } from '@/components/SummaryTile';
import { Select } from '@/components/ui/input';
import { Card, CardBody, CardHeader, Table, Th } from '@/components/ui/table';
import { cn } from '@/lib/cn';
import { fmtDateShort } from '@/lib/dates';
import { formatMoney, formatPct, formatQuantity, hrs, qty } from '@/lib/format';
import { sortRows, useTableSort } from '@/lib/sort';
import { useUrlState } from '@/lib/url-state';

import { DailyChart } from './DailyChart';
import { useJobContext } from './JobDetailPage';
import { useJobToDate } from './useJobs';

const DEFAULTS = { code: '', sort: '' };

/** W3 To-date: KPI tiles, production vs plan table with pace band, daily chart for one code. */
export function ToDateTab() {
  const { job } = useJobContext();
  const toDate = useJobToDate(job.id);
  const [state, setState] = useUrlState(DEFAULTS);
  const sort = useTableSort(state.sort, (v) => setState({ sort: v }));
  const [hover, setHover] = useState<string | null>(null);

  const data = toDate.data;
  const codes = useMemo(
    () => (data?.costCodes ?? []).filter((c) => !c.archived || Number(c.actualTotal) > 0),
    [data],
  );
  const selectedId =
    state.code || codes.find((c) => c.daysLogged > 0)?.costCodeId || codes[0]?.costCodeId || '';
  const selected = codes.find((c) => c.costCodeId === selectedId) ?? null;
  const sorted = useMemo(
    () =>
      sortRows(codes, sort.column, sort.dir, {
        code: (c) => c.code,
        actual: (c) => c.actualTotal,
        planned: (c) => c.plannedTotal,
        pct: (c) => c.pctComplete,
        days: (c) => c.daysLogged,
        avg: (c) => c.avgPerDay,
        plan: (c) => c.planPerDay,
        pace: (c) => c.pacePct,
      }),
    [codes, sort.column, sort.dir],
  );

  if (toDate.isPending) return <LoadingState />;
  if (toDate.isError)
    return <ErrorState error={toDate.error} onRetry={() => void toDate.refetch()} />;
  const d: JobToDateResponse = data!;
  const perDay = (total: string) =>
    d.daysWorked > 0 ? dec(total).div(d.daysWorked).toDecimalPlaces(1).toString() : '0';

  return (
    <>
      <div className="kpis">
        <SummaryTile
          label="Days worked"
          value={d.daysWorked}
          sub={d.firstDate ? `since ${fmtDateShort(d.firstDate)}` : 'no entries yet'}
        />
        <SummaryTile
          label="Labor hours"
          value={hrs(d.laborHours)}
          unit="h"
          sub={`${perDay(d.laborHours)} h / day`}
        />
        <SummaryTile
          label="Machine hours"
          value={hrs(d.machineHours)}
          unit="h"
          sub={`${perDay(d.machineHours)} h / day`}
        />
        <SummaryTile
          label="Labor cost · estimate"
          value={formatMoney(d.laborCostEstimate)}
          sub="from default hourly costs"
        />
        <SummaryTile
          label="Machine cost · estimate"
          value={formatMoney(d.machineCostEstimate)}
          sub="from default hourly costs"
        />
      </div>
      <div className="three">
        <Card>
          <CardHeader title="Production vs plan" />
          <Table>
            <thead>
              <tr>
                <Th sortKey="code" sort={sort}>
                  Code
                </Th>
                <Th>Description</Th>
                <Th right sortKey="actual" sort={sort}>
                  Actual
                </Th>
                <Th right sortKey="planned" sort={sort}>
                  Planned
                </Th>
                <Th right sortKey="pct" sort={sort}>
                  % done
                </Th>
                <Th right sortKey="days" sort={sort}>
                  Days
                </Th>
                <Th right sortKey="avg" sort={sort}>
                  Avg / day
                </Th>
                <Th right sortKey="plan" sort={sort}>
                  Plan / day
                </Th>
                <Th sortKey="pace" sort={sort}>
                  Pace
                </Th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <EmptyState>No cost codes on this job yet.</EmptyState>
                  </td>
                </tr>
              ) : null}
              {sorted.map((c) => (
                <tr
                  key={c.costCodeId}
                  className={cn('cursor-pointer', c.costCodeId === selectedId && 'hi')}
                  onClick={() => setState({ code: c.costCodeId })}
                  onMouseEnter={() => setHover(c.costCodeId)}
                  onMouseLeave={() => setHover(null)}
                  aria-selected={c.costCodeId === selectedId}
                >
                  <td>
                    <code className={cn(c.archived && 'text-ink3')}>{c.code}</code>
                  </td>
                  <td className={cn(c.archived && 'text-ink3')}>
                    {c.description}
                    {c.archived ? <span className="sub">archived</span> : null}
                  </td>
                  <td className="r">{qty(c.actualTotal, c.unit)}</td>
                  <td className="r">{qty(c.plannedTotal)}</td>
                  <td className="r">{formatPct(c.pctComplete)}</td>
                  <td className="r">{c.daysLogged}</td>
                  <td className="r">{c.avgPerDay === null ? '—' : formatQuantity(c.avgPerDay)}</td>
                  <td className="r">{qty(c.planPerDay)}</td>
                  <td>
                    <BandPill
                      band={c.paceBand}
                      pct={c.pacePct}
                      fallback={c.daysLogged === 0 ? 'not started' : 'no plan'}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
        <Card className="self-start">
          <CardHeader
            title={selected ? `Daily actual · ${selected.code}` : 'Daily actual'}
            right={
              <Select
                aria-label="Cost code"
                value={selectedId}
                onChange={(e) => setState({ code: e.target.value })}
                className="h-7 text-xs"
              >
                {codes.map((c) => (
                  <option key={c.costCodeId} value={c.costCodeId}>
                    {c.code} {c.description}
                  </option>
                ))}
              </Select>
            }
          />
          <CardBody>
            {selected ? (
              <DailyChart
                unit={selected.unit}
                planPerDay={selected.planPerDay}
                series={d.series[selected.costCodeId] ?? []}
                highlight={hover === selected.costCodeId}
              />
            ) : (
              <EmptyState>Pick a cost code.</EmptyState>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}
